import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetupPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/setup/${token}`)
      .then(r => setInfo(r.data))
      .catch(err => setError(err.response?.data?.detail || "Setup-Link ungültig"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { setError("Passwort muss mind. 8 Zeichen haben"); return; }
    if (pwd !== pwd2) { setError("Passwörter stimmen nicht überein"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/auth/setup/${token}`, { token, password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card border border-border rounded-sm p-8" data-testid="setup-page">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">signalSolution</p>
        <h1 className="font-heading text-2xl font-black tracking-tight mb-1">Passwort festlegen</h1>
        {loading && <p className="text-sm text-muted-foreground mt-4">Prüfe Einladung...</p>}
        {!loading && error && !info && (
          <div className="mt-6 flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-sm">
            <AlertCircle className="text-destructive shrink-0 mt-0.5" size={18} />
            <div>
              <p className="text-sm font-medium text-destructive">{error}</p>
              <Link to="/login" className="text-xs text-muted-foreground underline mt-1 inline-block">Zum Login</Link>
            </div>
          </div>
        )}
        {info && !done && (
          <>
            <p className="text-sm text-muted-foreground mt-2">
              Hi <strong className="text-foreground">{info.name || info.email.split("@")[0]}</strong> — leg dein Passwort fest, um loszulegen.
            </p>
            <p className="text-xs text-muted-foreground mt-1 font-mono">{info.email} · Rolle: {info.role}</p>
            <form onSubmit={submit} className="space-y-3 mt-6" data-testid="setup-form">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Neues Passwort</Label>
                <Input type="password" value={pwd} onChange={e => setPwd(e.target.value)} required minLength={8} className="rounded-sm" data-testid="pwd-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Passwort wiederholen</Label>
                <Input type="password" value={pwd2} onChange={e => setPwd2(e.target.value)} required minLength={8} className="rounded-sm" data-testid="pwd2-input" />
              </div>
              {error && <p className="text-xs text-destructive">{error}</p>}
              <Button type="submit" disabled={saving} className="w-full rounded-sm gap-2" data-testid="setup-submit">
                <Lock size={14} /> {saving ? "Speichere..." : "Passwort festlegen & einloggen"}
              </Button>
            </form>
          </>
        )}
        {done && (
          <div className="mt-6 flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-sm dark:bg-emerald-950/20 dark:border-emerald-900">
            <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={18} />
            <div>
              <p className="text-sm font-medium text-foreground">Passwort gesetzt!</p>
              <p className="text-xs text-muted-foreground mt-0.5">Du wirst gleich zum Login weitergeleitet...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
